import { HttpClient, HttpParams } from '@angular/common/http'
import { Observable } from 'rxjs'
import { forkJoin, throwError } from 'rxjs'
import { catchError, finalize, map } from 'rxjs/operators'

import * as models from '../data-models'
import { Services } from '../services'
import { DateUtil } from '../utility/date'
import { Repository } from './base/repository'

export class DataService {
    constructor(
        private services: Services,
        public http: HttpClient,
    ) {}

    currentUserId!: string
    defaultPortfolioId!: string
    defaultUrlRoute!: string
    dataRefresh!: string

    get<T>(url: string, params: HttpParams = new HttpParams(), deserializeType: any = null): Observable<T> {
        // The options can contain header and other information. See https://angular.io/guide/http
        const options = { params: params }
        return this.http.get(url, options).pipe(map(result => this.createInstances(result, deserializeType)))
    }

    put<T>(url: string, item: any, params: HttpParams = new HttpParams(), deserializeType: any = null): Observable<T> {
        const options = { params: params }
        return this.http.put(url, item, options).pipe(map(result => this.createInstances(result, deserializeType)))
    }

    post<T>(url: string, item: any, params: HttpParams = new HttpParams(), deserializeType: any = null): Observable<T> {
        const options = { params: params }
        return this.http.post(url, item, options).pipe(map(result => this.createInstances(result, deserializeType)))
    }

    private createInstances(result: any, deserializeType: any = null): any {
        if (deserializeType == null) {
            return result
        } else if (Array.isArray(result)) {
            const items: any[] = []
            result.forEach(item => items.push(this.services.utility.deserializeAs(item, deserializeType, this.services)))
            return items
        }

        return this.services.utility.deserializeAs(result, deserializeType, this.services)
    }

    load(showBusy: boolean, displayGenericError = true, ...queries: Observable<any>[]): Observable<any[]> {
        let loadId: any
        if (showBusy) {
            loadId = this.services.loading.begin()
        }

        return forkJoin(queries).pipe(
            map(results => {
                return results
            }),
            catchError(error => {
                if (error.status == 404) {
                    this.services.notification.info('You have been logged out due to inactivity')
                    this.services.authentication.logout()
                } else if (error.status == 403) {
                    this.services.notification.error('You do not have permissions')
                } else if (displayGenericError) {
                    this.services.notification.error('Failed to load data')
                }
                return throwError(error)
            }),
            finalize(() => {
                this.services.loading.end(loadId)
                this.services.utility.raiseLayoutRequiresUpdate()
            }),
        )
    }

    private _bid!: Repository<models.Bid>
    public get bid(): Repository<models.Bid> {
        if (!this._bid) {
            this._bid = new Repository<models.Bid>(this.services, models.Bid, 'Bid').configureSortBy(x => x.bidDate, true)
        }
        return this._bid
    }

    private _bidOutlooks!: Repository<models.BidOutlook>
    public get bidOutlooks(): Repository<models.BidOutlook> {
        if (!this._bidOutlooks) {
            this._bidOutlooks = new Repository<models.BidOutlook>(this.services, models.BidOutlook, 'BidOutlook').configureSortBy(
                x => x.bidDate ?? new Date(3000, 1, 1),
            ) // Sorting on bid date, but put nulls at the end.
        }
        return this._bidOutlooks
    }

    private _bidPrequalifications!: Repository<models.BidPrequalification>
    public get bidPrequalifications(): Repository<models.BidPrequalification> {
        if (!this._bidPrequalifications) {
            this._bidPrequalifications = new Repository<models.BidPrequalification>(
                this.services,
                models.BidPrequalification,
                'BidPrequalification',
            ).configureSortBy(x => x.name)
        }
        return this._bidPrequalifications
    }

    private _bidRanges!: Repository<models.BidRange>
    public get bidRanges(): Repository<models.BidRange> {
        if (!this._bidRanges) {
            this._bidRanges = new Repository<models.BidRange>(this.services, models.BidRange, 'BidRange').configureSortBy(x => x.low)
        }
        return this._bidRanges
    }

    private _blueStakesLogs!: Repository<models.BlueStakesLog>
    public get blueStakesLogs(): Repository<models.BlueStakesLog> {
        if (!this._blueStakesLogs) {
            this._blueStakesLogs = new Repository<models.BlueStakesLog>(
                this.services,
                models.BlueStakesLog,
                'BlueStakesLog',
            ).configureMultiSortBy(
                { key: x => x.isActive, isDescending: true },
                {
                    key: x => {
                        const t = x.ticketUpdateDate ? new Date(String(x.ticketUpdateDate)).getTime() : Number.MAX_SAFE_INTEGER
                        return Number.isNaN(t) ? Number.MAX_SAFE_INTEGER : t
                    },
                    isDescending: false,
                },
            )
        }
        return this._blueStakesLogs
    }

    private _swpppSites!: Repository<models.SwpppSite>
    public get swpppSites(): Repository<models.SwpppSite> {
        if (!this._swpppSites) {
            this._swpppSites = new Repository<models.SwpppSite>(this.services, models.SwpppSite, 'SwpppSite').configureSortBy(x => x.name)
        }
        return this._swpppSites
    }

    private _swpppSitePeople!: Repository<models.SwpppSitePerson>
    public get swpppSitePeople(): Repository<models.SwpppSitePerson> {
        if (!this._swpppSitePeople) {
            this._swpppSitePeople = new Repository<models.SwpppSitePerson>(this.services, models.SwpppSitePerson, 'SwpppSitePerson')
        }
        return this._swpppSitePeople
    }

    private _swpppDocuments!: Repository<models.SwpppDocument>
    public get swpppDocuments(): Repository<models.SwpppDocument> {
        if (!this._swpppDocuments) {
            this._swpppDocuments = new Repository<models.SwpppDocument>(this.services, models.SwpppDocument, 'SwpppDocument').configureSortBy(x => x.title)
        }
        return this._swpppDocuments
    }

    private _swpppActionItems!: Repository<models.SwpppActionItem>
    public get swpppActionItems(): Repository<models.SwpppActionItem> {
        if (!this._swpppActionItems) {
            this._swpppActionItems = new Repository<models.SwpppActionItem>(this.services, models.SwpppActionItem, 'SwpppActionItem')
        }
        return this._swpppActionItems
    }

    private _swpppNotes!: Repository<models.SwpppNote>
    public get swpppNotes(): Repository<models.SwpppNote> {
        if (!this._swpppNotes) {
            this._swpppNotes = new Repository<models.SwpppNote>(this.services, models.SwpppNote, 'SwpppNote')
        }
        return this._swpppNotes
    }

    private _swpppForms!: Repository<models.SwpppForm>
    public get swpppForms(): Repository<models.SwpppForm> {
        if (!this._swpppForms) {
            this._swpppForms = new Repository<models.SwpppForm>(this.services, models.SwpppForm, 'SwpppForm').configureSortBy(x => x.name)
        }
        return this._swpppForms
    }

    private _damagedUtilities!: Repository<models.DamagedUtility>
    public get damagedUtilities(): Repository<models.DamagedUtility> {
        if (!this._damagedUtilities) {
            this._damagedUtilities = new Repository<models.DamagedUtility>(
                this.services,
                models.DamagedUtility,
                'DamagedUtility',
            ).configureSortBy(x => {
                const t = x.damageDate ? new Date(String(x.damageDate)).getTime() : 0
                return Number.isNaN(t) ? 0 : t
            }, true)
        }
        return this._damagedUtilities
    }

    private _bonusTableYearlyVariables!: Repository<models.BonusTableYearlyVariable>
    public get bonusTableYearlyVariables(): Repository<models.BonusTableYearlyVariable> {
        if (!this._bonusTableYearlyVariables) {
            this._bonusTableYearlyVariables = new Repository<models.BonusTableYearlyVariable>(
                this.services,
                models.BonusTableYearlyVariable,
                'BonusTableYearlyVariable',
            )
        }
        return this._bonusTableYearlyVariables
    }

    private _businessPlanSections!: Repository<models.BusinessPlanSection>
    public get businessPlanSections(): Repository<models.BusinessPlanSection> {
        if (!this._businessPlanSections) {
            this._businessPlanSections = new Repository<models.BusinessPlanSection>(
                this.services,
                models.BusinessPlanSection,
                'BusinessPlanSection',
            ).configureSortBy(x => x.order)
        }
        return this._businessPlanSections
    }

    private _companyQuarters!: Repository<models.CompanyQuarter>
    public get companyQuarters(): Repository<models.CompanyQuarter> {
        if (!this._companyQuarters) {
            this._companyQuarters = new Repository<models.CompanyQuarter>(
                this.services,
                models.CompanyQuarter,
                'CompanyQuarter',
            ).configureSortBy(x => x.name)
        }
        return this._companyQuarters
    }

    private _counties!: Repository<models.County>
    public get counties(): Repository<models.County> {
        if (!this._counties) {
            this._counties = new Repository<models.County>(this.services, models.County, 'County').configureSortBy(x => x.name)
        }
        return this._counties
    }

    private _tireVendors!: Repository<models.TireVendor>
    public get tireVendors(): Repository<models.TireVendor> {
        if (!this._tireVendors) {
            this._tireVendors = new Repository<models.TireVendor>(this.services, models.TireVendor, 'TireVendor').configureSortBy(x => x.name)
        }
        return this._tireVendors
    }

    private _tireVendorRoutes!: Repository<models.TireVendorRoute>
    public get tireVendorRoutes(): Repository<models.TireVendorRoute> {
        if (!this._tireVendorRoutes) {
            this._tireVendorRoutes = new Repository<models.TireVendorRoute>(
                this.services,
                models.TireVendorRoute,
                'TireVendorRoute',
            ).configureMultiSortBy(
                { key: x => x.county, isDescending: false },
                { key: x => x.equipmentClass, isDescending: false },
                { key: x => x.rank, isDescending: false },
            )
        }
        return this._tireVendorRoutes
    }

    private _equipmentTireConfigs!: Repository<models.EquipmentTireConfig>
    public get equipmentTireConfigs(): Repository<models.EquipmentTireConfig> {
        if (!this._equipmentTireConfigs) {
            this._equipmentTireConfigs = new Repository<models.EquipmentTireConfig>(
                this.services,
                models.EquipmentTireConfig,
                'EquipmentTireConfig',
            ).configureMultiSortBy(
                { key: x => x.equipment, isDescending: false },
                { key: x => x.axleNumber, isDescending: false },
                { key: x => x.side, isDescending: false },
            )
        }
        return this._equipmentTireConfigs
    }

    private _equipmentFluidCapacities!: Repository<models.EquipmentFluidCapacity>
    public get equipmentFluidCapacities(): Repository<models.EquipmentFluidCapacity> {
        if (!this._equipmentFluidCapacities) {
            this._equipmentFluidCapacities = new Repository<models.EquipmentFluidCapacity>(
                this.services,
                models.EquipmentFluidCapacity,
                'EquipmentFluidCapacity',
            ).configureMultiSortBy(
                { key: x => x.compartment, isDescending: false },
                { key: x => x.equipment, isDescending: false },
            )
        }
        return this._equipmentFluidCapacities
    }

    private _equipmentGets!: Repository<models.EquipmentGet>
    public get equipmentGets(): Repository<models.EquipmentGet> {
        if (!this._equipmentGets) {
            this._equipmentGets = new Repository<models.EquipmentGet>(
                this.services,
                models.EquipmentGet,
                'EquipmentGet',
            ).configureSortBy(x => x.equipment)
        }
        return this._equipmentGets
    }

    private _equipmentBatteries!: Repository<models.EquipmentBattery>
    public get equipmentBatteries(): Repository<models.EquipmentBattery> {
        if (!this._equipmentBatteries) {
            this._equipmentBatteries = new Repository<models.EquipmentBattery>(
                this.services,
                models.EquipmentBattery,
                'EquipmentBattery',
            ).configureSortBy(x => x.equipment)
        }
        return this._equipmentBatteries
    }

    private _equipmentBrakes!: Repository<models.EquipmentBrakes>
    public get equipmentBrakes(): Repository<models.EquipmentBrakes> {
        if (!this._equipmentBrakes) {
            this._equipmentBrakes = new Repository<models.EquipmentBrakes>(
                this.services,
                models.EquipmentBrakes,
                'EquipmentBrakes',
            ).configureSortBy(x => x.equipment)
        }
        return this._equipmentBrakes
    }

    private _equipmentFilters!: Repository<models.EquipmentFilter>
    public get equipmentFilters(): Repository<models.EquipmentFilter> {
        if (!this._equipmentFilters) {
            this._equipmentFilters = new Repository<models.EquipmentFilter>(
                this.services,
                models.EquipmentFilter,
                'EquipmentFilter',
            ).configureMultiSortBy(
                { key: x => x.equipment, isDescending: false },
                { key: x => x.filterType, isDescending: false },
            )
        }
        return this._equipmentFilters
    }

    private _vistaUploads!: Repository<models.VistaUpload>
    public get vistaUploads(): Repository<models.VistaUpload> {
        if (!this._vistaUploads) {
            this._vistaUploads = new Repository<models.VistaUpload>(this.services, models.VistaUpload, 'VistaBatchImport').configureSortBy(
                x => x.createdOn,
                true,
            )
        }
        return this._vistaUploads
    }

    private _divisions!: Repository<models.Division>
    public get divisions(): Repository<models.Division> {
        if (!this._divisions) {
            this._divisions = new Repository<models.Division>(this.services, models.Division, 'Division').configureMultiSortBy(
                { key: x => x.sortOrder, isDescending: false },
                { key: x => x.name, isDescending: false },
            )
        }
        return this._divisions
    }

    private _divisionJobs!: Repository<models.DivisionJob>
    public get divisionJobs(): Repository<models.DivisionJob> {
        if (!this._divisionJobs) {
            this._divisionJobs = new Repository<models.DivisionJob>(this.services, models.DivisionJob, 'DivisionJob').configureSortBy(x => x.jobId)
        }
        return this._divisionJobs
    }

    private _fabShopStockProductTypes!: Repository<models.FabShopStockProductType>
    public get fabShopStockProductTypes(): Repository<models.FabShopStockProductType> {
        if (!this._fabShopStockProductTypes) {
            this._fabShopStockProductTypes = new Repository<models.FabShopStockProductType>(
                this.services,
                models.FabShopStockProductType,
                'FabShopStockProductType',
            ).configureMultiSortBy(
                { key: x => x.sortOrder, isDescending: false },
                { key: x => x.name, isDescending: false },
            )
        }
        return this._fabShopStockProductTypes
    }

    private _fabShopStockProductTypeMaterials!: Repository<models.FabShopStockProductTypeMaterial>
    public get fabShopStockProductTypeMaterials(): Repository<models.FabShopStockProductTypeMaterial> {
        if (!this._fabShopStockProductTypeMaterials) {
            this._fabShopStockProductTypeMaterials = new Repository<models.FabShopStockProductTypeMaterial>(
                this.services,
                models.FabShopStockProductTypeMaterial,
                'FabShopStockProductTypeMaterial',
            ).configureSortBy(x => x.material)
        }
        return this._fabShopStockProductTypeMaterials
    }

    private _equipmentBreakdowns!: Repository<models.EquipmentBreakdown>
    public get equipmentBreakdowns(): Repository<models.EquipmentBreakdown> {
        if (!this._equipmentBreakdowns) {
            this._equipmentBreakdowns = new Repository<models.EquipmentBreakdown>(
                this.services,
                models.EquipmentBreakdown,
                'EquipmentBreakdown',
            ).configureSortBy(x => x.completedDate !== null, false)
        }
        return this._equipmentBreakdowns
    }

    private _equipmentRequests!: Repository<models.EquipmentRequest>
    public get equipmentRequests(): Repository<models.EquipmentRequest> {
        if (!this._equipmentRequests) {
            this._equipmentRequests = new Repository<models.EquipmentRequest>(
                this.services,
                models.EquipmentRequest,
                'EquipmentRequest',
            ).configureSortBy(x => x.equipment, true)
        }
        return this._equipmentRequests
    }

    private _equipmentReports!: Repository<models.EquipmentReport>
    public get equipmentReports(): Repository<models.EquipmentReport> {
        if (!this._equipmentReports) {
            this._equipmentReports = new Repository<models.EquipmentReport>(
                this.services,
                models.EquipmentReport,
                'EquipmentReport',
            )
        }
        return this._equipmentReports
    }

    private _equipmentTranfers!: Repository<models.EquipmentTransfer>
    public get equipmentTranfers(): Repository<models.EquipmentTransfer> {
        if (!this._equipmentTranfers) {
            this._equipmentTranfers = new Repository<models.EquipmentTransfer>(
                this.services,
                models.EquipmentTransfer,
                'EquipmentTransfer',
            ).configureSortBy(x => x.isComplete, false)
        }
        return this._equipmentTranfers
    }

    private _fuelEntries!: Repository<models.FuelEntry>
    public get fuelEntries(): Repository<models.FuelEntry> {
        if (!this._fuelEntries) {
            this._fuelEntries = new Repository<models.FuelEntry>(this.services, models.FuelEntry, 'FuelEntry').configureSortBy(
                x => x.date,
                true,
            )
        }
        return this._fuelEntries
    }

    private _fuelPrices!: Repository<models.FuelPrice>
    public get fuelPrices(): Repository<models.FuelPrice> {
        if (!this._fuelPrices) {
            this._fuelPrices = new Repository<models.FuelPrice>(this.services, models.FuelPrice, 'FuelPrice')
        }
        return this._fuelPrices
    }

    private _fuelRecordDefaults!: Repository<models.FuelRecordDefault>
    public get fuelRecordDefaults(): Repository<models.FuelRecordDefault> {
        if (!this._fuelRecordDefaults) {
            this._fuelRecordDefaults = new Repository<models.FuelRecordDefault>(
                this.services,
                models.FuelRecordDefault,
                'FuelRecordDefault',
            ).configureSortBy(x => x.equipment)
        }
        return this._fuelRecordDefaults
    }

    private _groups!: Repository<models.Group>
    public get groups(): Repository<models.Group> {
        if (!this._groups) {
            this._groups = new Repository<models.Group>(this.services, models.Group, 'Group').configureSortBy(x => x.name)
        }
        return this._groups
    }

    // Note: This repo sends requests to a dummy endpoint, since we want to manage the collection on the client.
    private _groupCriteria!: Repository<models.GroupCriterion>
    public get groupCriteria(): Repository<models.GroupCriterion> {
        if (!this._groupCriteria) {
            this._groupCriteria = new Repository<models.GroupCriterion>(
                this.services,
                models.GroupCriterion,
                'GroupCriteria',
            ).configureSortBy(x => x.attributeName)
        }
        return this._groupCriteria
    }

    private _headlines!: Repository<models.Headline>
    public get headlines(): Repository<models.Headline> {
        if (!this._headlines) {
            this._headlines = new Repository<models.Headline>(this.services, models.Headline, 'Headline').configureMultiSortBy(
                { key: x => x.doneDate == null, isDescending: true }, // First, sort done ones to the bottom
                { key: x => x.rank, isDescending: false }, // Then, sort by rank
                { key: x => x.name }, // Finally, sort by name within the same rank
            )
        }
        return this._headlines
    }

    private _hrApplicantStatuses!: Repository<models.HrApplicantStatus>
    public get hrApplicantStatuses(): Repository<models.HrApplicantStatus> {
        if (!this._hrApplicantStatuses) {
            this._hrApplicantStatuses = new Repository<models.HrApplicantStatus>(
                this.services,
                models.HrApplicantStatus,
                'HrApplicantStatus',
            ).configureSortBy(x => x.candidateName)
        }
        return this._hrApplicantStatuses
    }

    private _hrHiringNeeds!: Repository<models.HrHiringNeed>
    public get hrHiringNeeds(): Repository<models.HrHiringNeed> {
        if (!this._hrHiringNeeds) {
            this._hrHiringNeeds = new Repository<models.HrHiringNeed>(this.services, models.HrHiringNeed, 'HrHiringNeed').configureSortBy(
                x => x.position,
            )
        }
        return this._hrHiringNeeds
    }

    private _hrGoals!: Repository<models.HrGoal>
    public get hrGoals(): Repository<models.HrGoal> {
        if (!this._hrGoals) {
            this._hrGoals = new Repository<models.HrGoal>(this.services, models.HrGoal, 'HrGoal').configureMultiSortBy(
                { key: x => x.userId, isDescending: true },
                { key: x => x.name, isDescending: false },
            )
        }
        return this._hrGoals
    }

    private _hrOffers!: Repository<models.HrOffer>
    public get hrOffers(): Repository<models.HrOffer> {
        if (!this._hrOffers) {
            this._hrOffers = new Repository<models.HrOffer>(this.services, models.HrOffer, 'HrOffer').configureSortBy(x => x.offerDate)
        }
        return this._hrOffers
    }

    private _hrSurveys!: Repository<models.HrSurvey>
    public get hrSurveys(): Repository<models.HrSurvey> {
        if (!this._hrSurveys) {
            this._hrSurveys = new Repository<models.HrSurvey>(this.services, models.HrSurvey, 'HrSurvey').configureSortBy(x => x.displayOrder)
        }
        return this._hrSurveys
    }

    private _jobTypeVendors!: Repository<models.JobTypeVendor>
    public get jobTypeVendors(): Repository<models.JobTypeVendor> {
        if (!this._jobTypeVendors) {
            this._jobTypeVendors = new Repository<models.JobTypeVendor>(
                this.services,
                models.JobTypeVendor,
                'JobTypeVendor',
            ).configureSortBy(x => x.vendorName)
        }
        return this._jobTypeVendors
    }

    private _jobTypes!: Repository<models.JobType>
    public get jobTypes(): Repository<models.JobType> {
        if (!this._jobTypes) {
            this._jobTypes = new Repository<models.JobType>(this.services, models.JobType, 'JobType').configureSortBy(x => x.shortName)
        }
        return this._jobTypes
    }

    private _jobOwnerships!: Repository<models.JobOwnership>
    public get jobOwnerships(): Repository<models.JobOwnership> {
        if (!this._jobOwnerships) {
            this._jobOwnerships = new Repository<models.JobOwnership>(this.services, models.JobOwnership, 'JobOwnership').configureSortBy(
                x => x.order,
            )
        }
        return this._jobOwnerships
    }

    private _jobOwnershipJobs!: Repository<models.JobOwnershipJob>
    public get jobOwnershipJobs(): Repository<models.JobOwnershipJob> {
        if (!this._jobOwnershipJobs) {
            this._jobOwnershipJobs = new Repository<models.JobOwnershipJob>(
                this.services,
                models.JobOwnershipJob,
                'JobOwnershipJob',
            ).configureSortBy(x => x.job)
        }
        return this._jobOwnershipJobs
    }

    private _jobOwnershipTemplates!: Repository<models.JobOwnershipTemplate>
    public get jobOwnershipTemplates(): Repository<models.JobOwnershipTemplate> {
        if (!this._jobOwnershipTemplates) {
            this._jobOwnershipTemplates = new Repository<models.JobOwnershipTemplate>(
                this.services,
                models.JobOwnershipTemplate,
                'JobOwnershipTemplate',
            ).configureSortBy(x => x.name)
        }
        return this._jobOwnershipTemplates
    }

    private _jobOwnershipTemplateItems!: Repository<models.JobOwnershipTemplateItem>
    public get jobOwnershipTemplateItems(): Repository<models.JobOwnershipTemplateItem> {
        if (!this._jobOwnershipTemplateItems) {
            this._jobOwnershipTemplateItems = new Repository<models.JobOwnershipTemplateItem>(
                this.services,
                models.JobOwnershipTemplateItem,
                'JobOwnershipTemplateItem',
            ).configureSortBy(x => x.order)
        }
        return this._jobOwnershipTemplateItems
    }

    private _manpowerMetricExclusions!: Repository<models.ManpowerMetricExclusion>
    public get manpowerMetricExclusions(): Repository<models.ManpowerMetricExclusion> {
        if (!this._manpowerMetricExclusions) {
            this._manpowerMetricExclusions = new Repository<models.ManpowerMetricExclusion>(
                this.services,
                models.ManpowerMetricExclusion,
                'ManpowerMetricExclusion',
            )
        }
        return this._manpowerMetricExclusions
    }

    private _materials!: Repository<any>
    public get materials(): Repository<any> {
        if (!this._materials) {
            this._materials = new Repository<any>(this.services, models.MaterialTicket, '')
        }
        return this._materials
    }

    private _materialLocationQuantities!: Repository<models.MaterialLocationQuantity>
    public get materialLocationQuantities(): Repository<models.MaterialLocationQuantity> {
        if (!this._materialLocationQuantities) {
            this._materialLocationQuantities = new Repository<models.MaterialLocationQuantity>(
                this.services,
                models.MaterialLocationQuantity,
                'MaterialLocationQuantity',
            ).configureSortBy(x => x.material)
        }
        return this._materialLocationQuantities
    }

    private _materialPits!: Repository<any>
    public get materialPits(): Repository<any> {
        if (!this._materialPits) this._materialPits = new Repository<any>(this.services, models.MaterialTicket, '')
        return this._materialPits
    }

    private _materialTickets!: Repository<models.MaterialTicket>
    public get materialTickets(): Repository<models.MaterialTicket> {
        if (!this._materialTickets) {
            this._materialTickets = new Repository<models.MaterialTicket>(
                this.services,
                models.MaterialTicket,
                'MaterialTicket',
            ).configureSortBy(x => x.date, true)
        }
        return this._materialTickets
    }

    private _pendingChangeOrders!: Repository<models.PendingChangeOrder>
    public get pendingChangeOrders(): Repository<models.PendingChangeOrder> {
        if (!this._pendingChangeOrders) {
            this._pendingChangeOrders = new Repository<models.PendingChangeOrder>(
                this.services,
                models.PendingChangeOrder,
                'PendingChangeOrder',
            ).configureSortBy(x => x.createdOn, true)
        }
        return this._pendingChangeOrders
    }

    private _jobHazardAnalyses!: Repository<models.JobHazardAnalysis>
    public get jobHazardAnalyses(): Repository<models.JobHazardAnalysis> {
        if (!this._jobHazardAnalyses) {
            this._jobHazardAnalyses = new Repository<models.JobHazardAnalysis>(
                this.services,
                models.JobHazardAnalysis,
                'JobHazardAnalysis',
            ).configureSortBy(x => x.date as any, true)
        }
        return this._jobHazardAnalyses
    }

    private _leadershipTeamVisits!: Repository<models.LeadershipTeamVisit>
    public get leadershipTeamVisits(): Repository<models.LeadershipTeamVisit> {
        if (!this._leadershipTeamVisits) {
            this._leadershipTeamVisits = new Repository<models.LeadershipTeamVisit>(
                this.services,
                models.LeadershipTeamVisit,
                'LeadershipTeamVisit',
            ).configureSortBy(x => x.visitDate as any, true)
        }
        return this._leadershipTeamVisits
    }

    private _vehicleInspections!: Repository<models.VehicleInspection>
    public get vehicleInspections(): Repository<models.VehicleInspection> {
        if (!this._vehicleInspections) {
            this._vehicleInspections = new Repository<models.VehicleInspection>(
                this.services,
                models.VehicleInspection,
                'VehicleInspection',
            ).configureSortBy(x => x.date as any, true)
        }
        return this._vehicleInspections
    }

    private _safetyFeedbacks!: Repository<models.SafetyFeedback>
    public get safetyFeedbacks(): Repository<models.SafetyFeedback> {
        if (!this._safetyFeedbacks) {
            this._safetyFeedbacks = new Repository<models.SafetyFeedback>(
                this.services,
                models.SafetyFeedback,
                'SafetyFeedback',
            ).configureSortBy(x => x.eventDateTime as any, true)
        }
        return this._safetyFeedbacks
    }

    private _autoAccidentReports!: Repository<models.AutoAccidentReport>
    public get autoAccidentReports(): Repository<models.AutoAccidentReport> {
        if (!this._autoAccidentReports) {
            this._autoAccidentReports = new Repository<models.AutoAccidentReport>(
                this.services,
                models.AutoAccidentReport,
                'AutoAccidentReport',
            ).configureSortBy(x => x.incidentDateTime as any, true)
        }
        return this._autoAccidentReports
    }

    private _companyDamageReports!: Repository<models.CompanyDamageReport>
    public get companyDamageReports(): Repository<models.CompanyDamageReport> {
        if (!this._companyDamageReports) {
            this._companyDamageReports = new Repository<models.CompanyDamageReport>(
                this.services,
                models.CompanyDamageReport,
                'CompanyDamageReport',
            ).configureSortBy(x => x.incidentDateTime as any, true)
        }
        return this._companyDamageReports
    }

    private _safetyCorrectiveActions!: Repository<models.SafetyCorrectiveAction>
    public get safetyCorrectiveActions(): Repository<models.SafetyCorrectiveAction> {
        if (!this._safetyCorrectiveActions) {
            this._safetyCorrectiveActions = new Repository<models.SafetyCorrectiveAction>(
                this.services,
                models.SafetyCorrectiveAction,
                'SafetyCorrectiveAction',
            ).configureSortBy(x => x.createdOn as any, true)
        }
        return this._safetyCorrectiveActions
    }

    private _safetyNearMisses!: Repository<models.SafetyNearMiss>
    public get safetyNearMisses(): Repository<models.SafetyNearMiss> {
        if (!this._safetyNearMisses) {
            this._safetyNearMisses = new Repository<models.SafetyNearMiss>(
                this.services,
                models.SafetyNearMiss,
                'SafetyNearMiss',
            ).configureSortBy(x => x.incidentDateTime as any, true)
        }
        return this._safetyNearMisses
    }

    private _safetyWorkAloneAuths!: Repository<models.SafetyWorkAloneAuth>
    public get safetyWorkAloneAuths(): Repository<models.SafetyWorkAloneAuth> {
        if (!this._safetyWorkAloneAuths) {
            this._safetyWorkAloneAuths = new Repository<models.SafetyWorkAloneAuth>(
                this.services,
                models.SafetyWorkAloneAuth,
                'SafetyWorkAloneAuth',
            ).configureSortBy(x => x.workDate as any, true)
        }
        return this._safetyWorkAloneAuths
    }

    private _dailySafetyCoordinationMeetings!: Repository<models.DailySafetyCoordinationMeeting>
    public get dailySafetyCoordinationMeetings(): Repository<models.DailySafetyCoordinationMeeting> {
        if (!this._dailySafetyCoordinationMeetings) {
            this._dailySafetyCoordinationMeetings = new Repository<models.DailySafetyCoordinationMeeting>(
                this.services,
                models.DailySafetyCoordinationMeeting,
                'DailySafetyCoordinationMeeting',
            ).configureSortBy(x => x.meetingDateTime as any, true)
        }
        return this._dailySafetyCoordinationMeetings
    }

    private _employeeInjuryReports!: Repository<models.EmployeeInjuryReport>
    public get employeeInjuryReports(): Repository<models.EmployeeInjuryReport> {
        if (!this._employeeInjuryReports) {
            this._employeeInjuryReports = new Repository<models.EmployeeInjuryReport>(
                this.services,
                models.EmployeeInjuryReport,
                'EmployeeInjuryReport',
            ).configureSortBy(x => x.injuryDateTime as any, true)
        }
        return this._employeeInjuryReports
    }

    private _confinedSpaceEntryPermits!: Repository<models.ConfinedSpaceEntryPermit>
    public get confinedSpaceEntryPermits(): Repository<models.ConfinedSpaceEntryPermit> {
        if (!this._confinedSpaceEntryPermits) {
            this._confinedSpaceEntryPermits = new Repository<models.ConfinedSpaceEntryPermit>(
                this.services,
                models.ConfinedSpaceEntryPermit,
                'ConfinedSpaceEntryPermit',
            ).configureSortBy(x => x.issuedDateTime as any, true)
        }
        return this._confinedSpaceEntryPermits
    }

    private _incidentInvestigations!: Repository<models.IncidentInvestigation>
    public get incidentInvestigations(): Repository<models.IncidentInvestigation> {
        if (!this._incidentInvestigations) {
            this._incidentInvestigations = new Repository<models.IncidentInvestigation>(
                this.services,
                models.IncidentInvestigation,
                'IncidentInvestigation',
            ).configureSortBy(x => x.incidentOn as any, true)
        }
        return this._incidentInvestigations
    }

    private _purchaseOrders!: Repository<models.PurchaseOrder>
    public get purchaseOrders(): Repository<models.PurchaseOrder> {
        if (!this._purchaseOrders) {
            this._purchaseOrders = new Repository<models.PurchaseOrder>(
                this.services,
                models.PurchaseOrder,
                'PurchaseOrder',
            ).configureSortBy(x => x.createdOn, true)
        }
        return this._purchaseOrders
    }

    private _purchasingOrders!: Repository<models.PurchasingOrder>
    public get purchasingOrders(): Repository<models.PurchasingOrder> {
        if (!this._purchasingOrders) {
            this._purchasingOrders = new Repository<models.PurchasingOrder>(
                this.services,
                models.PurchasingOrder,
                'PurchasingOrder',
            ).configureSortBy(x => x.createdOn, true)
        }
        return this._purchasingOrders
    }

    private _purchasingQuotes!: Repository<models.PurchasingQuote>
    public get purchasingQuotes(): Repository<models.PurchasingQuote> {
        if (!this._purchasingQuotes) {
            this._purchasingQuotes = new Repository<models.PurchasingQuote>(
                this.services,
                models.PurchasingQuote,
                'PurchasingQuote',
            ).configureSortBy(x => x.createdOn, true)
        }
        return this._purchasingQuotes
    }

    private _purchasingReceipts!: Repository<models.PurchasingReceipt>
    public get purchasingReceipts(): Repository<models.PurchasingReceipt> {
        if (!this._purchasingReceipts) {
            this._purchasingReceipts = new Repository<models.PurchasingReceipt>(
                this.services,
                models.PurchasingReceipt,
                'PurchasingReceipt',
            ).configureSortBy(x => x.createdOn, true)
        }
        return this._purchasingReceipts
    }

    private _purchasingInvoices!: Repository<models.PurchasingInvoice>
    public get purchasingInvoices(): Repository<models.PurchasingInvoice> {
        if (!this._purchasingInvoices) {
            this._purchasingInvoices = new Repository<models.PurchasingInvoice>(
                this.services,
                models.PurchasingInvoice,
                'PurchasingInvoice',
            ).configureSortBy(x => x.createdOn, true)
        }
        return this._purchasingInvoices
    }

    private _punchListItems!: Repository<models.PunchListItem>
    public get punchListItems(): Repository<models.PunchListItem> {
        if (!this._punchListItems) {
            this._punchListItems = new Repository<models.PunchListItem>(
                this.services,
                models.PunchListItem,
                'PunchList',
            ).configureSortBy(x => x.item ?? 0)
        }
        return this._punchListItems
    }

    private _proposalBids!: Repository<models.ProposalBid>
    public get proposalBids(): Repository<models.ProposalBid> {
        if (!this._proposalBids) {
            this._proposalBids = new Repository<models.ProposalBid>(this.services, models.ProposalBid, 'ProposalBid')
        }
        return this._proposalBids
    }

    private _proposalBidDetails!: Repository<models.ProposalBidDetail>
    public get proposalBidDetails(): Repository<models.ProposalBidDetail> {
        if (!this._proposalBidDetails) {
            this._proposalBidDetails = new Repository<models.ProposalBidDetail>(
                this.services,
                models.ProposalBidDetail,
                'ProposalBidDetail',
            )
        }
        return this._proposalBidDetails
    }

    private _materialTransfers!: Repository<models.MaterialTransfer>
    public get materialTransfers(): Repository<models.MaterialTransfer> {
        if (!this._materialTransfers) {
            this._materialTransfers = new Repository<models.MaterialTransfer>(
                this.services,
                models.MaterialTransfer,
                'MaterialTransfer',
            ).configureSortBy(x => x.date, true)
        }
        return this._materialTransfers
    }

    private _meetings!: Repository<models.Meeting>
    public get meetings(): Repository<models.Meeting> {
        if (!this._meetings) {
            this._meetings = new Repository<models.Meeting>(this.services, models.Meeting, 'Meeting').configureSortBy(x => x.name)
        }
        return this._meetings
    }

    private _meetingPage!: Repository<models.MeetingPage>
    public get meetingPage(): Repository<models.MeetingPage> {
        if (!this._meetingPage) {
            this._meetingPage = new Repository<models.MeetingPage>(this.services, models.MeetingPage, 'MeetingPage').configureSortBy(
                x => x.order,
            )
        }
        return this._meetingPage
    }

    private _monthlyPlans!: Repository<models.MonthlyPlan>
    public get monthlyPlans(): Repository<models.MonthlyPlan> {
        if (!this._monthlyPlans) {
            this._monthlyPlans = new Repository<models.MonthlyPlan>(this.services, models.MonthlyPlan, 'MonthlyPlan').configureSortBy(
                x => x.date,
            )
        }
        return this._monthlyPlans
    }

    private _notifications!: Repository<models.Notification>
    public get notifications(): Repository<models.Notification> {
        if (!this._notifications) {
            this._notifications = new Repository<models.Notification>(this.services, models.Notification, 'Notification').configureSortBy(
                x => x.title,
            )
        }
        return this._notifications
    }

    private _notificationInstances!: Repository<models.NotificationInstance>
    public get notificationInstances(): Repository<models.NotificationInstance> {
        if (!this._notificationInstances) {
            this._notificationInstances = new Repository<models.NotificationInstance>(
                this.services,
                models.NotificationInstance,
                'NotificationInstance',
            ).configureSortBy(x => DateUtil.toTimestamp(x.sentDate), true)
        }
        return this._notificationInstances
    }

    private _rentals!: Repository<models.Rental>
    public get rentals(): Repository<models.Rental> {
        if (!this._rentals) {
            this._rentals = new Repository<models.Rental>(this.services, models.Rental, 'Rental').configureMultiSortBy(
                { key: x => x.returnedDate == null, isDescending: true },
                { key: x => x.returnedDate, isDescending: true },
                { key: x => x.scheduledReturnDate, isDescending: false },
                { key: x => x.pickedUpDate, isDescending: false },
            )
        }
        return this._rentals
    }

    private _retentionMitigations!: Repository<models.RetentionMitigation>
    public get retentionMitigations(): Repository<models.RetentionMitigation> {
        if (!this._retentionMitigations) {
            this._retentionMitigations = new Repository<models.RetentionMitigation>(
                this.services,
                models.RetentionMitigation,
                'Retention',
            ).configureSortBy(x => x.description)
        }
        return this._retentionMitigations
    }

    private _rights!: Repository<models.Right>
    public get rights(): Repository<models.Right> {
        if (!this._rights) {
            this._rights = new Repository<models.Right>(this.services, models.Right, 'Right').configureSortBy(x => x.name)
        }
        return this._rights
    }

    private _rocks!: Repository<models.Rock>
    public get rocks(): Repository<models.Rock> {
        if (!this._rocks) {
            this._rocks = new Repository<models.Rock>(this.services, models.Rock, 'Rock').configureMultiSortBy(
                { key: x => x.userId, isDescending: true },
                { key: x => x.dueDate, isDescending: false },
            )
        }
        return this._rocks
    }

    private _rockMilestones!: Repository<models.RockMilestone>
    public get rockMilestones(): Repository<models.RockMilestone> {
        if (!this._rockMilestones) {
            this._rockMilestones = new Repository<models.RockMilestone>(
                this.services,
                models.RockMilestone,
                'RockMilestone',
            ).configureSortBy(x => x.dueDate, true)
        }
        return this._rockMilestones
    }

    private _rockDueDateHistories!: Repository<models.RockDueDateHistory>
    public get rockDueDateHistories(): Repository<models.RockDueDateHistory> {
        if (!this._rockDueDateHistories) {
            this._rockDueDateHistories = new Repository<models.RockDueDateHistory>(
                this.services,
                models.RockDueDateHistory,
                'RockDueDateHistory',
            ).configureSortBy(x => x.changedOn, true)
        }
        return this._rockDueDateHistories
    }

    private _quarterlyMeasurables!: Repository<models.QuarterlyMeasurable>
    public get quarterlyMeasurables(): Repository<models.QuarterlyMeasurable> {
        if (!this._quarterlyMeasurables) {
            this._quarterlyMeasurables = new Repository<models.QuarterlyMeasurable>(
                this.services,
                models.QuarterlyMeasurable,
                'QuarterlyMeasurable',
            ).configureSortBy(x => x.order, false)
        }
        return this._quarterlyMeasurables
    }

    private _roles!: Repository<models.Role>
    public get roles(): Repository<models.Role> {
        if (!this._roles) {
            this._roles = new Repository<models.Role>(this.services, models.Role, 'Role').configureSortBy(x => x.name)
        }
        return this._roles
    }

    private _safetyMeetings!: Repository<models.SafetyMeeting>
    public get safetyMeetings(): Repository<models.SafetyMeeting> {
        if (!this._safetyMeetings) {
            this._safetyMeetings = new Repository<models.SafetyMeeting>(
                this.services,
                models.SafetyMeeting,
                'SafetyMeeting',
            ).configureSortBy(x => x.scheduledDate, true)
        }
        return this._safetyMeetings
    }

    private _subcontractorTrainingModules!: Repository<models.SubcontractorTrainingModule>
    public get subcontractorTrainingModules(): Repository<models.SubcontractorTrainingModule> {
        if (!this._subcontractorTrainingModules) {
            this._subcontractorTrainingModules = new Repository<models.SubcontractorTrainingModule>(
                this.services,
                models.SubcontractorTrainingModule,
                'SubcontractorTrainingModule',
            ).configureSortBy(x => x.sortOrder)
        }
        return this._subcontractorTrainingModules
    }

    private _subcontractorTrainingInvites!: Repository<models.SubcontractorTrainingTrackingRow>
    public get subcontractorTrainingInvites(): Repository<models.SubcontractorTrainingTrackingRow> {
        if (!this._subcontractorTrainingInvites) {
            this._subcontractorTrainingInvites = new Repository<models.SubcontractorTrainingTrackingRow>(
                this.services,
                models.SubcontractorTrainingTrackingRow,
                'SubcontractorTrainingInvite',
            )
        }
        return this._subcontractorTrainingInvites
    }

    private _safetyMitigations!: Repository<models.SafetyMitigation>
    public get safetyMitigations(): Repository<models.SafetyMitigation> {
        if (!this._safetyMitigations) {
            this._safetyMitigations = new Repository<models.SafetyMitigation>(
                this.services,
                models.SafetyMitigation,
                'Safety',
            ).configureSortBy(x => x.description)
        }
        return this._safetyMitigations
    }

    private _schedulingJobs!: Repository<models.SchedulingJob>
    public get schedulingJobs(): Repository<models.SchedulingJob> {
        if (!this._schedulingJobs) {
            this._schedulingJobs = new Repository<models.SchedulingJob>(
                this.services,
                models.SchedulingJob,
                'SchedulingJob',
            ).configureSortBy(x => x.priority)
        }
        return this._schedulingJobs
    }

    private _schedulingTasks!: Repository<models.SchedulingTask>
    public get schedulingTasks(): Repository<models.SchedulingTask> {
        if (!this._schedulingTasks) {
            this._schedulingTasks = new Repository<models.SchedulingTask>(
                this.services,
                models.SchedulingTask,
                'SchedulingTask',
            ).configureSortBy(x => x.priority)
        }
        return this._schedulingTasks
    }

    private _schedulingTransports!: Repository<models.SchedulingTransport>
    public get schedulingTransports(): Repository<models.SchedulingTransport> {
        if (!this._schedulingTransports) {
            this._schedulingTransports = new Repository<models.SchedulingTransport>(
                this.services,
                models.SchedulingTransport,
                'SchedulingTransport',
            ).configureMultiSortBy(
                { key: x => x.completionDate == null, isDescending: true },
                { key: x => x.priority, isDescending: false },
            )
        }
        return this._schedulingTransports
    }

    private _smallToolLocations!: Repository<models.MaterialLocationQuantity>
    public get smallToolLocations(): Repository<models.MaterialLocationQuantity> {
        if (!this._smallToolLocations) {
            this._smallToolLocations = new Repository<models.MaterialLocationQuantity>(
                this.services,
                models.MaterialLocationQuantity,
                'MaterialLocationQuantity',
            ).configureSortBy(x => x.material)
        }
        return this._smallToolLocations
    }

    private _toDos!: Repository<models.ToDo>
    public get toDos(): Repository<models.ToDo> {
        if (!this._toDos) {
            this._toDos = new Repository<models.ToDo>(this.services, models.ToDo, 'ToDo').configureMultiSortBy(
                { key: x => x.userId, isDescending: true },
                { key: x => x.dueDate, isDescending: false },
            )
        }
        return this._toDos
    }

    private _users!: Repository<models.User>
    public get users(): Repository<models.User> {
        if (!this._users) {
            this._users = new Repository<models.User>(this.services, models.User, 'User').configureSortBy(x => x.active, true)
        }
        return this._users
    }

    private _vendors!: Repository<models.Vendor>
    public get vendors(): Repository<models.Vendor> {
        if (!this._vendors) {
            this._vendors = new Repository<models.Vendor>(this.services, models.Vendor, 'Vendor').configureSortBy(x => x.name)
        }
        return this._vendors
    }

    private _vpJobs!: Repository<models.VpJob>
    public get vpJobs(): Repository<models.VpJob> {
        if (!this._vpJobs) {
            this._vpJobs = new Repository<models.VpJob>(this.services, models.VpJob, 'VPJob')
                // This sort will do the job number descending, but put the five digit jobs at the end.
                .configureMultiSortBy({ key: x => x.id.trim().length, isDescending: false }, { key: x => x.id, isDescending: true })
        }
        return this._vpJobs
    }

    private _qualityControlConcretes!: Repository<models.QualityControlConcrete>
    public get qualityControlConcretes(): Repository<models.QualityControlConcrete> {
        if (!this._qualityControlConcretes) {
            this._qualityControlConcretes = new Repository<models.QualityControlConcrete>(
                this.services,
                models.QualityControlConcrete,
                'QualityControl',
            ).configureSortBy(x => x.structureType)
        }
        return this._qualityControlConcretes
    }

    private _qualityControlDirts!: Repository<models.QualityControlDirt>
    public get qualityControlDirts(): Repository<models.QualityControlDirt> {
        if (!this._qualityControlDirts) {
            this._qualityControlDirts = new Repository<models.QualityControlDirt>(
                this.services,
                models.QualityControlDirt,
                'QualityControlDirt',
            ).configureSortBy(x => x.descriptionOfWork)
        }
        return this._qualityControlDirts
    }

    private _qualityControlPipes!: Repository<models.QualityControlPipe>
    public get qualityControlPipes(): Repository<models.QualityControlPipe> {
        if (!this._qualityControlPipes) {
            this._qualityControlPipes = new Repository<models.QualityControlPipe>(
                this.services,
                models.QualityControlPipe,
                'QualityControlPipe',
            ).configureSortBy(x => x.typeOfPipe)
        }
        return this._qualityControlPipes
    }

    private _qualityControlRoads!: Repository<models.QualityControlRoad>
    public get qualityControlRoads(): Repository<models.QualityControlRoad> {
        if (!this._qualityControlRoads) {
            this._qualityControlRoads = new Repository<models.QualityControlRoad>(
                this.services,
                models.QualityControlRoad,
                'QualityControlRoad',
            ).configureSortBy(x => x.streetAreaName)
        }
        return this._qualityControlRoads
    }

    private _qualityControlMechanicals!: Repository<models.QualityControlMechanical>
    public get qualityControlMechanicals(): Repository<models.QualityControlMechanical> {
        if (!this._qualityControlMechanicals) {
            this._qualityControlMechanicals = new Repository<models.QualityControlMechanical>(
                this.services,
                models.QualityControlMechanical,
                'QualityControlMechanical',
            ).configureSortBy(x => x.boltNutWasherMaterialType)
        }
        return this._qualityControlMechanicals
    }

    private _qualityControlPaintings!: Repository<models.QualityControlPainting>
    public get qualityControlPaintings(): Repository<models.QualityControlPainting> {
        if (!this._qualityControlPaintings) {
            this._qualityControlPaintings = new Repository<models.QualityControlPainting>(
                this.services,
                models.QualityControlPainting,
                'QualityControlPainting',
            ).configureSortBy(x => x.productName)
        }
        return this._qualityControlPaintings
    }

    private _qualityControlCathodics!: Repository<models.QualityControlCathodic>
    public get qualityControlCathodics(): Repository<models.QualityControlCathodic> {
        if (!this._qualityControlCathodics) {
            this._qualityControlCathodics = new Repository<models.QualityControlCathodic>(
                this.services,
                models.QualityControlCathodic,
                'QualityControlCathodic',
            ).configureSortBy(x => x.installationLocation)
        }
        return this._qualityControlCathodics
    }

    private _qualityControlConcreteResults!: Repository<models.QualityControlConcreteResult>
    public get qualityControlConcreteResults(): Repository<models.QualityControlConcreteResult> {
        if (!this._qualityControlConcreteResults) {
            this._qualityControlConcreteResults = new Repository<models.QualityControlConcreteResult>(
                this.services,
                models.QualityControlConcreteResult,
                'QualityControlConcreteResult',
            ).configureSortBy(x => x.reportDate)
        }
        return this._qualityControlConcreteResults
    }

    private _qualityControlSoilResults!: Repository<models.QualityControlSoilResult>
    public get qualityControlSoilResults(): Repository<models.QualityControlSoilResult> {
        if (!this._qualityControlSoilResults) {
            this._qualityControlSoilResults = new Repository<models.QualityControlSoilResult>(
                this.services,
                models.QualityControlSoilResult,
                'QualityControlSoilResult',
            ).configureSortBy(x => x.reportDate)
        }
        return this._qualityControlSoilResults
    }

    private _planComments!: Repository<models.PlanComment>
    public get planComments(): Repository<models.PlanComment> {
        if (!this._planComments) {
            this._planComments = new Repository<models.PlanComment>(
                this.services,
                models.PlanComment,
                'PlanComment',
            ).configureSortBy(x => x.createdOn, true)
        }
        return this._planComments
    }

    private _disciplinaryActions!: Repository<models.DisciplinaryAction>
    public get disciplinaryActions(): Repository<models.DisciplinaryAction> {
        if (!this._disciplinaryActions) {
            this._disciplinaryActions = new Repository<models.DisciplinaryAction>(
                this.services,
                models.DisciplinaryAction,
                'DisciplinaryAction',
            ).configureSortBy(x => x.createdOn, true)
        }
        return this._disciplinaryActions
    }

    private _fabProcessSteps!: Repository<models.FabProcessStep>
    public get fabProcessSteps(): Repository<models.FabProcessStep> {
        if (!this._fabProcessSteps) {
            this._fabProcessSteps = new Repository<models.FabProcessStep>(
                this.services,
                models.FabProcessStep,
                'FabProcessStep',
            ).configureSortBy(x => x.sortOrder)
        }
        return this._fabProcessSteps
    }

    private _fabJobs!: Repository<models.FabJob>
    public get fabJobs(): Repository<models.FabJob> {
        if (!this._fabJobs) {
            this._fabJobs = new Repository<models.FabJob>(this.services, models.FabJob, 'FabJob').configureSortBy(x => x.displayName)
        }
        return this._fabJobs
    }

    private _fabPieces!: Repository<models.FabPiece>
    public get fabPieces(): Repository<models.FabPiece> {
        if (!this._fabPieces) {
            this._fabPieces = new Repository<models.FabPiece>(this.services, models.FabPiece, 'FabPiece').configureSortBy(x => x.name)
        }
        return this._fabPieces
    }

    private _fabHoldReasons!: Repository<models.FabHoldReason>
    public get fabHoldReasons(): Repository<models.FabHoldReason> {
        if (!this._fabHoldReasons) {
            this._fabHoldReasons = new Repository<models.FabHoldReason>(
                this.services,
                models.FabHoldReason,
                'FabHoldReason',
            ).configureSortBy(x => x.sortOrder)
        }
        return this._fabHoldReasons
    }

    private _fabHolds!: Repository<models.FabHold>
    public get fabHolds(): Repository<models.FabHold> {
        if (!this._fabHolds) {
            this._fabHolds = new Repository<models.FabHold>(this.services, models.FabHold, 'FabHold').configureSortBy(
                x => x.startedOn,
                true,
            )
        }
        return this._fabHolds
    }

    // We never add/edit/delete, so why use a full repo?
    //    private _vpUsers!: Repository<models.VpUser>;
    //    public get vpUsers(): Repository<models.VpUser> {
    //        if (!this._vpUsers) {
    //            this._vpUsers = new Repository<models.VpUser>(this.services, models.VpUser, 'VpUser')
    //                .configureSortBy(x => x.name);
    //        }
    //        return this._vpUsers;
    //    }
}
